import * as React from 'react';

import {
  enumSalesItemLineType,
  enumSalesLoadLineItemPhase,
  enumUnit,
} from '@rematter/frontend-lib-api-client';
import { Button, cn, ContentSection } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucidePlus } from 'lucide-react';

import { BrokeredLoadRequestedTable } from '~/modules/brokerage/_components/_shared/LineItems';
import {
  $brokeredLoadCreateForm,
  $brokeredOrderLinkedOrders,
  $createFormSalesOrderLineItems,
} from '~/modules/brokerage/_state/brokeredLoadCreateForm.state';
import { useBrokeredLoadCreateLineItemsManager } from '~/modules/brokerage/hooks/useBrokeredLoadCreateLineItemsManager';
import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import { enumBrokeredLoadWeightType } from '~/modules/brokerage/types/brokeredLoad';

type BrokeredLoadWizardStepMaterialsProps = {
  disabled?: boolean;
};

export const BrokeredLoadWizardStepMaterials = ({
  disabled,
}: BrokeredLoadWizardStepMaterialsProps) => {
  const t = useTranslations();
  const form = useAtomValue($brokeredLoadCreateForm);
  const linkedOrders = useAtomValue($brokeredOrderLinkedOrders);
  const { addItem, addItems, updateItem, deleteItem } =
    useBrokeredLoadCreateLineItemsManager();

  const linkedPoIds = React.useMemo(() => {
    if (!form.brokeredOrderId || !linkedOrders?.data) return undefined;
    return linkedOrders.data.purchaseOrderIds;
  }, [form.brokeredOrderId, linkedOrders?.data]);

  const salesOrderLineItemsQuery = useAtomValue($createFormSalesOrderLineItems);
  const salesOrderLineItems = salesOrderLineItemsQuery?.data;

  const handleAddMaterial = React.useCallback(() => {
    addItem({
      phase: enumSalesLoadLineItemPhase.Requested,
      ...(linkedPoIds?.length === 1 && { purchaseOrderId: linkedPoIds[0] }),
    });
  }, [addItem, linkedPoIds]);

  const handleAddAllFromSalesOrder = React.useCallback(() => {
    if (!salesOrderLineItems?.length) return;

    const newItems = salesOrderLineItems.map((soItem) => ({
      itemId: soItem.item.id,
      type: enumSalesItemLineType.Raw,
      phase: enumSalesLoadLineItemPhase.Requested,
      unitPrice: soItem.unitPrice
        ? {
            amount: soItem.unitPrice.amount,
            currencyCode: soItem.unitPrice.currencyCode,
            __typename: 'Money' as const,
          }
        : null,
      pricingUnit: soItem.unit ?? enumUnit.Pounds,
      weights: {
        [enumBrokeredLoadWeightType.Net]: soItem.quantity
          ? {
              value: soItem.quantity,
              unit: soItem.unit ?? enumUnit.Pounds,
              __typename: 'Weight' as const,
            }
          : undefined,
      },
      ...(linkedPoIds?.length === 1 && { purchaseOrderId: linkedPoIds[0] }),
    }));

    addItems(newItems);
  }, [salesOrderLineItems, addItems, linkedPoIds]);

  const requestedItems = form.lineItems.filter(
    (item) =>
      item.phase === enumSalesLoadLineItemPhase.Requested || !item.phase,
  );

  const showSalesOrderButton =
    form.salesOrderId && salesOrderLineItems && salesOrderLineItems.length > 0;

  if (requestedItems.length === 0) {
    return (
      <ContentSection
        className={cn('@BrokeredLoadWizardStepMaterials', '!border-t-0 !pt-0')}
      >
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-foreground/20 p-8">
          <span className="text-foreground/50">
            {t.brokerage.loads.detail.materials.emptyTitle}
          </span>
          <span className="text-sm text-neutral/50">
            {t.brokerage.loads.detail.materials.emptyDescription}
          </span>
          <div className="mt-3 flex justify-center gap-3">
            <Button
              variant="outline"
              color="primary"
              onPress={handleAddMaterial}
              isDisabled={disabled}
              size="large"
            >
              <LucidePlus className="size-4" />
              {t.brokerage.loads.detail.materials.addMaterial}
            </Button>
            {showSalesOrderButton && (
              <Button
                variant="outline"
                color="neutral"
                onPress={handleAddAllFromSalesOrder}
                isDisabled={disabled}
                size="large"
              >
                {t.brokerage.loads.detail.materials.addAllFromSalesOrder}
              </Button>
            )}
          </div>
        </div>
      </ContentSection>
    );
  }

  return (
    <ContentSection
      className={cn('@BrokeredLoadWizardStepMaterials', '!border-t-0 !pt-0')}
    >
      <div className="flex flex-col gap-2">
        <BrokeredLoadRequestedTable
          lineItems={requestedItems}
          onAddLineItem={handleAddMaterial}
          onUpdateLineItem={updateItem}
          onDeleteLineItem={deleteItem}
          disabled={disabled}
          purchaseOrderIds={linkedPoIds}
        />

        <div>
          {showSalesOrderButton && (
            <Button
              variant="outline"
              color="neutral"
              onPress={handleAddAllFromSalesOrder}
              isDisabled={disabled}
            >
              {t.brokerage.loads.detail.materials.addAllFromSalesOrder}
            </Button>
          )}
        </div>
      </div>
    </ContentSection>
  );
};
